'use strict';

/**
 * Angular directive to mount React 19.2 components within Angular templates.
 * 
 * Usage in Angular templates:
 *   <react-component component="ExampleComponent" props="{ name: 'World' }"></react-component>
 * 
 * The component must be registered in the componentRegistry below.
 */

import { createRoot } from 'react-dom/client';
import { createElement } from 'react';
import { MuiProvider } from './styles/MuiProvider';

// Registry of available React components
const componentRegistry = {};

/**
 * Register a React component for use in Angular templates
 * @param {string} name - The component name to use in templates
 * @param {React.ComponentType} component - The React component
 */
export function registerReactComponent(name, component) {
  componentRegistry[name] = component;
}

/**
 * Get a registered React component by name
 * @param {string} name - The component name
 * @returns {React.ComponentType|undefined}
 */
export function getReactComponent(name) {
  return componentRegistry[name];
}

// Angular module and directive
angular.module('client.react', [])
  .directive('reactComponent', function() {
    return {
      restrict: 'E',
      scope: {
        component: '@',
        props: '<'
      },
      link: function(scope, element) {
        let root = null;
        const container = element[0];

        function render() {
          const Component = componentRegistry[scope.component];
          
          if (!Component) {
            console.warn(`React component "${scope.component}" not found in registry`);
            return;
          }

          const props = scope.props || {};

          if (!root) {
            root = createRoot(container);
          }

          root.render(
            createElement(MuiProvider, null,
              createElement(Component, props)
            )
          );
        }

        // Initial render
        render();

        // Re-render when component or props change
        scope.$watch('component', function(newVal, oldVal) {
          if (newVal !== oldVal) {
            render();
          }
        });

        scope.$watch('props', function(newVal, oldVal) {
          if (newVal !== oldVal) {
            render();
          }
        }, true);

        // Cleanup on scope destroy
        scope.$on('$destroy', function() {
          if (root) {
            root.unmount();
            root = null;
          }
        });
      }
    };
  });
